import React from "react";
import { Link } from "react-router-dom";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "../ui/accordion";
import { ArrowUpRight, Phone, Mail } from "lucide-react";
import CTAButton from "./CTAButton";
import Photo from "./Photo";
import ShootPlanner from "./ShootPlanner";
import QuoteForm from "./QuoteForm";

// ─── PLANNER BLOCK ────────────────────────────────────
export const PlannerBlock = ({ eyebrow, headline, italic, blurb }) => (
  <div className="bg-bone">
    <div className="mx-auto max-w-[1440px] px-6 lg:px-10 pt-20 lg:pt-28 pb-0">
      <div className="max-w-3xl">
        <p className="text-xs sm:text-sm uppercase tracking-wider2 text-ember mb-5 font-bold">
          {eyebrow}
        </p>
        <h2 className="font-display tracking-editorial leading-[0.95]" style={{ fontSize: "clamp(2.25rem, 5vw, 4rem)" }}>
          {headline}
          <br />
          <span className="italic font-light text-ember">{italic}</span>
        </h2>
        <p className="mt-6 text-base md:text-lg text-char leading-relaxed">{blurb}</p>
      </div>
    </div>
    <ShootPlanner />
  </div>
);

// ─── MELBOURNE COVERAGE ───────────────────────────────
const DEFAULT_SUBURBS = [
  ["Melbourne CBD", "City restaurants, hotel kitchens, hospitality groups"],
  ["Brunswick", "Cafés, wine bars, neighbourhood restaurants"],
  ["Richmond", "Bridge Road and Swan Street venues, high-end dining"],
  ["Carlton", "Lygon Street restaurants and Italian institutions"],
  ["Footscray", "West-side food scene — Vietnamese, Ethiopian, Indian"],
  ["Glen Waverley", "Asian dining strip, family restaurants"],
  ["Dandenong", "South-east hospitality — Afghan, Indian, Sri Lankan"],
  ["Fitzroy", "Brunswick Street venues and inner-north bars"],
];

export const MelbourneSection = ({ serviceLabel = "Photography", eyebrow, headline, italic, intro, suburbs = DEFAULT_SUBURBS }) => (
  <section className="bg-ink text-bone py-24 lg:py-36">
    <div className="mx-auto max-w-[1440px] px-6 lg:px-10">
      <div className="grid grid-cols-12 gap-8 lg:gap-16">
        <div className="col-span-12 lg:col-span-5 lg:sticky lg:top-32 self-start">
          <p className="text-xs sm:text-sm uppercase tracking-wider2 text-ember mb-6 font-bold">
            {eyebrow}
          </p>
          <h2 className="font-display tracking-editorial leading-[0.95]" style={{ fontSize: "clamp(2.25rem, 5vw, 4.5rem)" }}>
            {headline}
            <br />
            <span className="italic font-light text-ember">{italic}</span>
          </h2>
          <p className="mt-8 text-bone/80 leading-relaxed max-w-md text-base md:text-lg">
            {intro}
          </p>
          <a href="#enquiry" className="inline-block mt-10">
            <CTAButton variant="ghost" size="md">Check coverage &amp; pricing</CTAButton>
          </a>
        </div>

        <div className="col-span-12 lg:col-span-7 space-y-px bg-bone/10 border border-bone/10">
          {suburbs.map(([name, sub]) => (
            <div key={name} className="bg-ink hover:bg-[#161613] transition-colors p-6 md:p-8 flex items-baseline justify-between gap-6 group">
              <div>
                <h3 className="font-display text-2xl md:text-3xl tracking-editorial group-hover:text-ember transition-colors">
                  {serviceLabel} {name}
                </h3>
                <p className="mt-2 text-sm md:text-base text-bone/65 leading-relaxed">{sub}</p>
              </div>
              <ArrowUpRight size={18} strokeWidth={1.6} className="text-bone/40 group-hover:text-ember group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all shrink-0 mt-2" />
            </div>
          ))}
        </div>
      </div>
    </div>
  </section>
);

// ─── FAQ ──────────────────────────────────────────────
export const FAQSection = ({ eyebrow, headline, italic, intro, faqs }) => (
  <section className="bg-bone text-ink py-24 lg:py-36">
    <div className="mx-auto max-w-[1440px] px-6 lg:px-10">
      <div className="grid grid-cols-12 gap-8 lg:gap-16 mb-12 lg:mb-16 items-end">
        <div className="col-span-12 lg:col-span-7">
          <p className="text-xs sm:text-sm uppercase tracking-wider2 text-ember mb-6 font-bold">
            {eyebrow}
          </p>
          <h2 className="font-display tracking-editorial leading-[0.95]" style={{ fontSize: "clamp(2.25rem, 5.5vw, 4.75rem)" }}>
            {headline}
            <br />
            <span className="italic font-light text-ember">{italic}</span>
          </h2>
        </div>
        <div className="col-span-12 lg:col-span-5 lg:pb-3">
          <p className="text-char text-base md:text-lg leading-relaxed mb-5">{intro}</p>
          <a href="tel:0494756111">
            <CTAButton variant="secondary" size="md">Call 0494 756 111</CTAButton>
          </a>
        </div>
      </div>

      <Accordion type="single" collapsible className="border-t border-ink/15">
        {faqs.map((f, i) => (
          <AccordionItem key={i} value={`faq-${i}`} className="border-b border-ink/15">
            <AccordionTrigger className="text-left py-6 md:py-8 hover:no-underline group">
              <div className="flex items-baseline gap-5 md:gap-8 pr-4 flex-1">
                <span className="font-sans font-bold text-sm md:text-base text-ember tracking-tight shrink-0">{String(i + 1).padStart(2, "0")}</span>
                <span className="font-display text-xl md:text-2xl lg:text-3xl tracking-editorial leading-tight group-hover:text-ember transition-colors">{f.q}</span>
              </div>
            </AccordionTrigger>
            <AccordionContent className="pb-8 pl-12 md:pl-16 pr-4 md:pr-12">
              <p className="text-base md:text-lg text-char leading-relaxed max-w-3xl">{f.a}</p>
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </div>
  </section>
);

// ─── FINAL CTA ────────────────────────────────────────
export const FinalCTA = ({ eyebrow, headline, italic, intro, backdropSlug = "hero-banquet-01", formTitle = "Tell us about your shoot." }) => (
  <section id="enquiry" className="relative bg-ink text-bone overflow-hidden">
    <div className="absolute inset-0 opacity-30">
      <Photo slug={backdropSlug} alt="" ratio="auto" className="!relative h-full w-full" />
    </div>
    <div className="absolute inset-0 bg-gradient-to-b from-ink via-ink/85 to-ink" />

    <div className="relative mx-auto max-w-[1440px] px-6 lg:px-10 py-24 lg:py-36">
      <div className="grid grid-cols-12 gap-10 lg:gap-16">
        <div className="col-span-12 lg:col-span-5">
          <p className="text-xs sm:text-sm uppercase tracking-wider2 text-ember mb-6 font-bold">
            {eyebrow}
          </p>
          <h2 className="font-display tracking-editorial leading-[0.95]" style={{ fontSize: "clamp(2.25rem, 5.5vw, 4.75rem)" }}>
            {headline}
            <br />
            <span className="italic font-light text-ember">{italic}</span>
          </h2>
          <p className="mt-8 text-base md:text-lg text-bone/80 leading-relaxed max-w-md">{intro}</p>

          <div className="mt-10 space-y-5 border-t border-bone/15 pt-8">
            <a href="tel:0494756111" className="group flex items-center gap-4">
              <Phone size={16} strokeWidth={1.6} className="text-ember" />
              <div>
                <p className="text-xs uppercase tracking-wider2 text-bone/55 mb-1 font-semibold">Direct</p>
                <p className="font-sans font-bold text-2xl tracking-tight group-hover:text-ember transition-colors">0494 756 111</p>
              </div>
            </a>
            <a href="mailto:hello@restaurantphotographer.com.au" className="group flex items-center gap-4">
              <Mail size={16} strokeWidth={1.6} className="text-ember" />
              <div>
                <p className="text-xs uppercase tracking-wider2 text-bone/55 mb-1 font-semibold">Email</p>
                <p className="font-display text-lg md:text-xl tracking-editorial group-hover:text-ember transition-colors break-all">hello@restaurantphotographer.com.au</p>
              </div>
            </a>
          </div>
        </div>

        <div className="col-span-12 lg:col-span-7">
          <div className="bg-bone text-ink p-8 lg:p-12">
            <p className="text-xs uppercase tracking-wider2 text-ember mb-3 font-bold">Request a shoot quote</p>
            <h3 className="font-display text-2xl md:text-3xl tracking-editorial leading-tight mb-8">{formTitle}</h3>
            <QuoteForm />
          </div>
        </div>
      </div>
    </div>
  </section>
);

// ─── BREADCRUMB (small reusable) ─────────────────────
export const Breadcrumb = ({ current }) => (
  <div className="hidden sm:flex items-center justify-between text-bone/70 mb-8 lg:mb-12">
    <p className="text-xs uppercase tracking-wider2 font-bold">
      <Link to="/" className="opacity-60 hover:opacity-100">Home</Link>
      <span className="text-ember mx-3">/</span>
      <span>{current}</span>
    </p>
    <p className="hidden lg:block text-xs uppercase tracking-wider2 font-semibold">
      Melbourne · VIC
    </p>
  </div>
);

// ─── SISTER SERVICES — bottom cross-link rail ────────
const ALL_SERVICES = {
  menu: { label: "Menu Photography", to: "/menu-photography", slug: "hero-laddu-220", blurb: "Full-menu image libraries — built for Uber Eats, your website and Google." },
  food: { label: "Food & Beverage", to: "/food-photography", slug: "menu-malai-tikka", blurb: "Hero plates and signature dishes for marketing, launches and editorial." },
  venue: { label: "Venue & Interior", to: "/venue-photography", slug: "hero-banquet-01", blurb: "Front of house, fit-out and ambience photography — real service, real tables." },
  brand: { label: "Brand & Campaign", to: "/brand-photography", slug: "hero-banquet-01", blurb: "Multi-frame campaigns for openings, PR, franchise rollouts and editorials." },
  ubereats: { label: "Uber Eats Photography", to: "/ubereats-photography", slug: "menu-butter-chicken", blurb: "Square-crop, app-graded photography for Uber Eats, DoorDash and Menulog." },
  cafe: { label: "Cafe Photography", to: "/cafe-photography", slug: "hero-spread-01", blurb: "Daylight-led photography for cafés, brunch venues and coffee shops." },
  gbp: { label: "Google Business Photos", to: "/google-business-profile-photos", slug: "venue-buffet-01", blurb: "Cover, interior, exterior and dish photography sized for Google Maps." },
  reels: { label: "Restaurant Reels", to: "/restaurant-reels", slug: "menu-falooda", blurb: "Short-form vertical video — pours, plating, service moments, hero clips." },
};

const DEFAULT_KEYS = ["menu", "food", "venue", "brand"];

export const SisterServices = ({ exclude, keys }) => {
  const list = (keys || DEFAULT_KEYS).filter((k) => k !== exclude && ALL_SERVICES[k]);
  const items = list.map((k) => ({ key: k, ...ALL_SERVICES[k] }));
  return (
    <section className="bg-ink text-bone py-20 lg:py-28 border-t border-bone/10">
      <div className="mx-auto max-w-[1440px] px-6 lg:px-10">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-12">
          <div>
            <p className="text-xs uppercase tracking-wider2 text-ember mb-4 font-bold">
              Also offered · One photographer, one library
            </p>
            <h2 className="font-display tracking-editorial leading-[0.95]" style={{ fontSize: "clamp(2rem, 4vw, 3.25rem)" }}>
              Other ways we shoot for
              <span className="italic font-light text-ember"> Melbourne hospitality.</span>
            </h2>
          </div>
        </div>
        <div className={`grid grid-cols-1 md:grid-cols-2 ${items.length >= 4 ? "lg:grid-cols-4" : "lg:grid-cols-3"} gap-px bg-bone/10 border border-bone/10`}>
          {items.map((s) => (
            <Link
              key={s.key}
              to={s.to}
              data-testid={`sister-link-${s.key}`}
              className="group bg-ink hover:bg-[#161613] transition-colors p-6 md:p-8 flex flex-col"
            >
              <Photo slug={s.slug} alt={`${s.label} Melbourne`} ratio="4/5" sizes="(min-width: 1024px) 22vw, 100vw" />
              <div className="mt-5 flex items-start justify-between gap-3">
                <h3 className="font-display text-xl md:text-2xl tracking-editorial leading-tight group-hover:text-ember transition-colors">
                  {s.label}
                </h3>
                <ArrowUpRight size={18} strokeWidth={1.6} className="text-bone/40 group-hover:text-ember group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all shrink-0 mt-1" />
              </div>
              <p className="mt-3 text-sm text-bone/70 leading-relaxed">{s.blurb}</p>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};
